import React, {Component, Fragment} from 'react';
import {RouteComponentProps, withRouter} from 'react-router';
import {BookingSettings, DraftBooking} from 'services/entities/booking';
import {Space} from 'services/entities/spaces';
import {CurrencyAmount} from 'util/currency';
import {loadSpace} from 'services/spaces';
import {loadBookingPrice} from 'services/bookings';
import {Button} from '@material-ui/core';
import {BookingStep, getNextStep, getStepURL} from '../../steps';
import './index.scss';
import {loadSettings} from 'services/pricing';
import AddDiscountForm from '../AddDiscountForm';
import {canModifyBookingDiscounts, getBookingHash} from 'util/booking';

interface BookingFooterProps extends RouteComponentProps {
  booking: DraftBooking|null;
  currentStep: BookingStep;
}

interface BookingFooterState {
  space: Space|null;
  price: CurrencyAmount|null,
  bookingHash: string|null,
  bookingSettings: BookingSettings|null
}

class BookingFooter extends Component<BookingFooterProps, BookingFooterState> {
  state: BookingFooterState = {
    space: null,
    price: null,
    bookingHash: null,
    bookingSettings: null
  };

  componentDidMount(): void {
    loadSettings().then(
      settings => this.setState({bookingSettings: settings})
    );
  }

  shouldComponentUpdate(nextProps: Readonly<BookingFooterProps>, nextState: Readonly<BookingFooterState>, nextContext: any): boolean {
    if(this.props.currentStep !== nextProps.currentStep) {
      return true;
    } else if(this.state.space !== nextState.space) {
      return true;
    } else if(!!this.state.price !== !!nextState.price || (nextState.price && !nextState.price.equals(this.state.price!))) {
      return true;
    } else if(!!this.props.booking !== !!nextProps.booking || (this.props.booking && this.state.bookingHash !== getBookingHash(nextProps.booking!))) {
      return true;
    } else {
      return false;
    }
  }

  componentDidUpdate(prevProps: Readonly<BookingFooterProps>, prevState: Readonly<BookingFooterState>, snapshot?: any): void {
    const space: Space|null = this.state.space;
    const booking = this.props.booking;
    const currentSpaceCode = space ? space!.code : null;
    const newSpaceCode = booking ? booking.space : null;
    if(currentSpaceCode && !newSpaceCode) {
      this.setState({space: null});
    } else if(newSpaceCode && newSpaceCode !== currentSpaceCode) {
      loadSpace(newSpaceCode).then(
        space => this.setState({space: space})
      );
    }
    this.tryLoadPrice();
  }

  tryLoadPrice(): boolean {
    const booking = this.props.booking;
    if (!booking) {
      return false;
    }
    const bookingHash = getBookingHash(booking);
    if(bookingHash === this.state.bookingHash) {
      return false;
    }
    this.setState({bookingHash: bookingHash});
    if (booking.space && booking.times.length > 0) {
      loadBookingPrice(booking, true, true).then(
        price => this.setState({price: price})
      );
    } else {
      this.setState({price: null});
    }
    return true;
  }

  onDiscountsChanged = () => {
    this.tryLoadPrice();
  };

  private onNextClick = () => {
    const booking = this.props.booking;
    const settings = this.state.bookingSettings;
    const nextStep = booking && settings ? getNextStep(this.props.currentStep, booking, settings) : null;
    if(!nextStep) {
      return;
    }
    const bookingCode = this.props.booking ? this.props.booking.code : null;
    this.props.history.push(getStepURL(nextStep, bookingCode));
  };

  render() {
    const space:Space|null = this.state.space;
    const price:CurrencyAmount|null = this.state.price;
    const booking = this.props.booking;
    const settings = this.state.bookingSettings;
    const discountsEnabled = booking && canModifyBookingDiscounts(booking);
    const nextStep = booking && settings ? getNextStep(this.props.currentStep, booking, settings) : null;
    return <div className="booking-footer">
      <div className="content">
        <div className="space">{ space && <Fragment>
          <span className="label">My Booking at</span>
          <span className="value">{ space!.name }</span>
        </Fragment> }</div>
        <div className="price">
          <Fragment>
            <span className="label">Total cost</span>
            <span className="value">{ price ? price!.format('$0.00') + ' VAT Incl.' : '-' }</span>
          </Fragment>
        </div>
        { discountsEnabled && <AddDiscountForm booking={booking} onDiscountsChanged={this.onDiscountsChanged} /> }
        <div className="actions">
          { <Button className="next" onClick={this.onNextClick} disabled={!nextStep}>Next</Button> }
        </div>
      </div>
    </div>;
  }
}

export default withRouter(BookingFooter);